'use client';
/**
 * GLISSER-DÉPOSER DU PLANNING — Lot 6 (02/09/2026).
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ── La décision d'architecture, arbitrée et non rouverte ───────────────────
 *
 * `@dnd-kit/core` PAR-DESSUS le `<table>` existant. Aucune bibliothèque de
 * calendrier ni de scheduler : toutes (FullCalendar, DayPilot, DHTMLX, Bryntum)
 * sont des timelines horizontales où l'axe X est du temps continu et où un
 * événement a un début et une fin. Ici l'axe X est fait de sept colonnes
 * DISCRÈTES et l'unité métier est un poste-jour sans horaire. Les adopter
 * imposerait de reconstruire par-dessus leur API toute la logique métier —
 * habilitations, calendrier d'ouverture, compteurs, propagation, recopie.
 *
 * ── Le point central : VALIDER PENDANT LE GLISSÉ, pas au dépôt ─────────────
 *
 * Les cases valides sont calculées dès `onDragStart` et signalées à l'œil
 * immédiatement. Un glisser-déposer qui laisse déposer puis affiche une erreur
 * est un mauvais glisser-déposer : il fait faire le geste pour le refuser.
 *
 * ── Ce qui n'est PAS négociable ────────────────────────────────────────────
 *
 *  · la mutation appelée est CELLE QUI EXISTE (`affecter` / `retirerAffectation`),
 *    jamais une route parallèle ;
 *  · un échec serveur RESTAURE l'affichage. L'écran ne doit jamais montrer un
 *    planning différent de celui qui est enregistré ;
 *  · le déplacement est une modification d'affectation comme une autre : MÊME
 *    trace au journal. C'est l'oubli classique quand le glissé est ajouté après
 *    coup — et la trace vient ici des deux routes existantes, qui la posent
 *    déjà, précisément parce qu'on ne les contourne pas.
 */
import React from 'react';
import {
  DndContext, DragOverlay, PointerSensor, TouchSensor, KeyboardSensor,
  useSensor, useSensors, useDraggable, useDroppable,
  type DragStartEvent, type DragEndEvent,
} from '@dnd-kit/core';

export interface CibleCase { posteCode: string; date: string }
export interface AffectationGlissee {
  documentId: string; orgId: string; nom: string; posteCode: string; date: string;
}

/* ── Ce que l'écran fournit, et ce qu'il reçoit ──────────────────────────── */
export interface ContexteDnd {
  /** Vrai quand le glissé est permis — hors mode modifiable, aucun geste. */
  actif: boolean;
  /** Une case est-elle une cible valide pour cette affectation ? */
  valide: (a: AffectationGlissee, c: CibleCase) => { ok: boolean; motif?: string };
  /** Déplacer réellement. Rend `false` pour que l'appelant restaure. */
  deplacer: (a: AffectationGlissee, c: CibleCase) => Promise<boolean>;
}

const Ctx = React.createContext<{
  actif: boolean;
  porte: AffectationGlissee | null;
  cibleValide: (c: CibleCase) => boolean;
} | null>(null);

export const useDnd = () => React.useContext(Ctx);

const cleCase = (c: CibleCase) => `case:${c.posteCode}|${c.date}`;

/**
 * L'enveloppe, posée sur LA TABLE SEULEMENT — jamais sur la page.
 *
 * ⚠️ Les capteurs portent une contrainte d'activation, et ce n'est pas un
 * réglage de confort : sans `distance` au pointeur et sans `delay` au toucher,
 * le DÉFILEMENT de la page devient impossible sur tablette — chaque amorce de
 * glissement vertical est comprise comme un déplacement d'affectation.
 */
export function PlanningDndContext({ ctx, children }: { ctx: ContexteDnd; children: React.ReactNode }) {
  const [porte, setPorte] = React.useState<AffectationGlissee | null>(null);

  const capteurs = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
    useSensor(KeyboardSensor),
  );

  const cibleValide = React.useCallback((c: CibleCase) => {
    if (!porte) return false;
    if (porte.posteCode === c.posteCode && porte.date === c.date) return false;
    return ctx.valide(porte, c).ok;
  }, [porte, ctx]);

  const onDragStart = (e: DragStartEvent) => {
    setPorte((e.active.data.current as AffectationGlissee) || null);
  };

  const onDragEnd = async (e: DragEndEvent) => {
    const a = (e.active.data.current as AffectationGlissee) || null;
    setPorte(null);
    /* Cible nulle ou invalide → on annule SANS appel réseau. Envoyer pour se
       faire refuser ferait un aller-retour, un temps d'attente et un message
       d'erreur là où il n'y avait qu'un geste abandonné. */
    if (!a || !e.over) return;
    const c = (e.over.data.current as CibleCase) || null;
    if (!c) return;
    if (a.posteCode === c.posteCode && a.date === c.date) return;
    if (!ctx.valide(a, c).ok) return;
    await ctx.deplacer(a, c);
  };

  return (
    <Ctx.Provider value={{ actif: ctx.actif, porte, cibleValide }}>
      <DndContext
        sensors={capteurs}
        onDragStart={onDragStart}
        onDragEnd={(e) => { void onDragEnd(e); }}
        onDragCancel={() => setPorte(null)}
        /* Les annonces sont EN FRANÇAIS et nomment la personne : « élément
           déplaçable » ne dit rien à qui ne voit pas la grille. */
        accessibility={{
          announcements: {
            onDragStart: ({ active }) => {
              const a = active.data.current as AffectationGlissee;
              return `Déplacement de ${a?.nom} commencé, depuis ${a?.posteCode} le ${a?.date}.`;
            },
            onDragOver: ({ over }) => {
              const c = over?.data.current as CibleCase | undefined;
              return c ? `Au-dessus de ${c.posteCode} le ${c.date}.` : 'Hors de toute case.';
            },
            onDragEnd: ({ active, over }) => {
              const a = active.data.current as AffectationGlissee;
              const c = over?.data.current as CibleCase | undefined;
              return c ? `${a?.nom} déposée sur ${c.posteCode} le ${c.date}.` : 'Déplacement abandonné.';
            },
            onDragCancel: () => 'Déplacement annulé, rien n’a changé.',
          },
        }}
      >
        {children}
        {/* Le FANTÔME, et non l'élément d'origine déplacé dans le DOM : sortir
            une cellule de son tableau pendant le glissé casserait la mise en
            page de la ligne entière. */}
        <DragOverlay dropAnimation={null}>
          {porte && (
            <div style={{
              padding: '4px 9px', borderRadius: 'var(--radius-sm)', fontSize: 12.5, fontWeight: 600,
              background: 'var(--cyan-50)', border: '1px solid var(--color-primary)',
              boxShadow: '0 6px 18px rgba(15,23,42,0.18)', cursor: 'grabbing',
            }}>{porte.nom}</div>
          )}
        </DragOverlay>
      </DndContext>
    </Ctx.Provider>
  );
}

/** Une affectation saisissable. Hors mode édition, elle rend ses enfants tels quels. */
export function AffectationGlissable({ a, children }: { a: AffectationGlissee; children: React.ReactNode }) {
  const dnd = useDnd();
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `aff:${a.documentId}`, data: a, disabled: !dnd?.actif,
  });
  if (!dnd?.actif) return <>{children}</>;
  return (
    <div ref={setNodeRef} {...listeners} {...attributes}
      /* L'ORIGINE reste en place, estompée : la faire disparaître ferait
         remonter toute la colonne pendant le glissé, et la case d'arrivée
         bougerait sous le doigt. */
      style={{ opacity: isDragging ? 0.35 : 1, cursor: 'grab', touchAction: 'manipulation' }}>
      {children}
    </div>
  );
}

/** Une case qui accepte un dépôt, et qui DIT pendant le glissé si elle l'accepte. */
export function CaseDeposable({ cible, children, style, titre, onClick }: {
  cible: CibleCase; children: React.ReactNode; style?: React.CSSProperties;
  /** L'infobulle de la case — l'état d'ouverture, tel que la grille l'écrivait. */
  titre?: string;
  /** Le clic de SÉLECTION (Lot 5). Il vit ici parce que la case est ce
   *  composant : le laisser sur un `<td>` parent créerait deux cases. */
  onClick?: (e: React.MouseEvent) => void;
}) {
  const dnd = useDnd();
  const { setNodeRef, isOver } = useDroppable({
    id: cleCase(cible), data: cible, disabled: !dnd?.actif,
  });
  const glisseEnCours = !!dnd?.porte;
  const accepte = glisseEnCours && dnd.cibleValide(cible);
  return (
    <td ref={setNodeRef} title={titre} onClick={onClick} style={{
      ...style,
      /* ⚠️ LA VALIDITÉ SE VOIT DÈS LE DÉBUT DU GLISSÉ, pas au survol. C'est
         tout l'intérêt : on sait où l'on peut aller avant d'y aller. Les cases
         refusées s'estompent au lieu de rougir — vingt cases rouges feraient
         lire un écran en faute, alors qu'il ne fait que dire ce qui est
         possible. */
      ...(glisseEnCours && !accepte ? { opacity: 0.4 } : {}),
      ...(accepte ? { outline: '2px dashed var(--color-primary)', outlineOffset: -2 } : {}),
      ...(isOver && accepte ? { background: 'var(--cyan-50)', outline: '2px solid var(--color-primary)' } : {}),
    }}>{children}</td>
  );
}
